import { Event } from "../../pojo/Event";
import { Item } from "../../pojo/Item";
import { S25Util } from "../../util/s25-util";
import type {
    OccurrenceScheduleAssignment,
    OccurrenceScheduleObject,
    OccurrenceScheduleRow,
    OccurrenceScheduleValue,
} from "./occurrence-object-schedule.types";
import Occurrence = Event.Occurrence;

export function expandOccurrenceRows(
    rawOccurrences: Occurrence[],
    profileCode: string,
    startAnchor: Date,
    endAnchor: Date,
): OccurrenceScheduleRow[] {
    const occurrences = S25Util.array.forceArray(rawOccurrences);
    if (!occurrences.length) {
        return [];
    }
    const shouldAnchorTimes = profileCode && profileCode !== "dnr";
    const durationMin = S25Util.date.diffMinutes(startAnchor, endAnchor);
    return occurrences.map((occurrence, index) => {
        let startDt = occurrence.evStartDt;
        let endDt = occurrence.evEndDt;
        if (shouldAnchorTimes) {
            startDt = S25Util.date.copyTime(new Date(startDt), startAnchor);
            endDt = S25Util.date.addMinutes(startDt, durationMin);
        }
        return {
            occId: index + 1,
            startDt,
            endDt,
        };
    });
}

export function mergeDefaultAndPerOccurrence(
    occurrences: OccurrenceScheduleRow[],
    defaultObjects: OccurrenceScheduleObject[],
    perOccurrenceOverrides: ReadonlyMap<number, OccurrenceScheduleObject[]>,
    customizedOccIds: ReadonlySet<number>,
): OccurrenceScheduleAssignment[] {
    return occurrences.map((row) => ({
        occId: row.occId,
        objects: customizedOccIds.has(row.occId)
            ? [...(perOccurrenceOverrides.get(row.occId) ?? [])]
            : [...defaultObjects],
    }));
}

export function buildScheduleValue(
    profileCode: string,
    occurrences: OccurrenceScheduleRow[],
    objectsForAllOccurrences: OccurrenceScheduleObject[],
    perOccurrenceOverrides: ReadonlyMap<number, OccurrenceScheduleObject[]>,
    customizedOccIds: ReadonlySet<number>,
): OccurrenceScheduleValue {
    return {
        profileCode,
        occurrences,
        objectsForAllOccurrences: [...objectsForAllOccurrences],
        assignments: mergeDefaultAndPerOccurrence(
            occurrences,
            objectsForAllOccurrences,
            perOccurrenceOverrides,
            customizedOccIds,
        ),
    };
}

export function toQuotaInspectLocationPairs(
    assignments: OccurrenceScheduleAssignment[],
): { roomId: number; occId: number }[] {
    const pairs: { roomId: number; occId: number }[] = [];
    for (const assignment of assignments) {
        for (const scheduleObject of assignment.objects) {
            if (scheduleObject.itemTypeId === Item.Ids.Location && scheduleObject.itemId) {
                pairs.push({
                    roomId: scheduleObject.itemId,
                    occId: assignment.occId,
                });
            }
        }
    }
    return pairs;
}

export function objectsMatch(left: OccurrenceScheduleObject[], right: OccurrenceScheduleObject[]): boolean {
    if (left.length !== right.length) {
        return false;
    }
    const leftIds = left.map((object) => object.itemId).sort((first, second) => first - second);
    const rightIds = right.map((object) => object.itemId).sort((first, second) => first - second);
    return leftIds.every((itemId, index) => itemId === rightIds[index]);
}

export function createDefaultScheduleScenario(): { startDt: Date; endDt: Date } {
    const startDt = new Date();
    startDt.setMinutes(0, 0, 0);
    const endDt = new Date(startDt);
    endDt.setHours(endDt.getHours() + 1);
    return { startDt, endDt };
}

export function createInitialScheduleValue(startDt: Date, endDt: Date): OccurrenceScheduleValue {
    return buildScheduleValue("dnr", [{ occId: 1, startDt, endDt }], [], new Map(), new Set());
}
